import merge from 'lodash/merge'

import {getJwt} from '../auth'
import {resolveJson, parseBody} from './helpers'
import * as report from '../report'
import * as URLS from '../urls'
import SERVICES from '../config/services'

const DELETE = 'DELETE'
const GET = 'GET'
const POST = 'POST'
const PUT = 'PUT'

// slip in sentry error reporting

const snitch = (url, opts) =>
  fetch(url, opts).catch((e) => {
    // send an error to the cloud
    report.exception(e, {
      extra: {url, opts: parseBody(opts)}, // with OPTIONS
    })

    // throw the error and pretend this never happened
    throw e
  })

// your basic fetch call that carries a JSON payload; implicitly a GET

const json = (url, payload, opts = {}) =>
  snitch(
    url,
    !payload || (opts && opts.method === GET)
      ? opts
      : merge(
          {
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
          opts,
        ),
  ).then(resolveJson)

// the below "get" functions take an `opts` in the place of `payload`...
// so we're going to shift some arguments around

const gettify =
  (fn) =>
  (url, opts, ...args) =>
    fn(url, undefined, opts, ...args)

// No Auth / Third-Party
// ----------------------------------------------------------------------------

// any requests to the auth service or third parties should be made via these functions
// builds off `json`

const meth = (method, url, payload, opts = {}) =>
  json(
    url,
    payload,
    merge({method}, opts), // add explicit request type
  )

const _get = (...args) => meth(GET, ...args) // private function provides an unmolested path

export const del = (...args) => meth(DELETE, ...args)
export const get = gettify(_get) // exported function rejiggers parameters
export const post = (...args) => meth(POST, ...args)
export const put = (...args) => meth(PUT, ...args)

// Services
// ----------------------------------------------------------------------------

// all requests to microservices should be made via these functions
// builds off `del`, `get`, `post`, and `put`

const auth = (fn, url, payload, opts = {}) =>
  fn(
    url,
    payload,
    merge(
      {
        headers: {
          Authorization: `jwt ${getJwt()}`, // attach JWT to request
        },
      },
      opts,
    ),
  )

const _authGet = (...args) => auth(_get, ...args) // private function provides an unmolested path

export const authDel = (...args) => auth(del, ...args)
export const authGet = gettify(_authGet) // exported function rejiggers parameters
export const authPost = (...args) => auth(post, ...args)
export const authPut = (...args) => auth(put, ...args)

// Monolith
// ----------------------------------------------------------------------------

const handle4xx = (response) => {
  if (response.status === 401) {
    // JWT invalid, force user to login again
    window.location = `${URLS.STATIC_LOGIN}?next=${window.location.pathname}&expired=true`
  }
  return response
}

// all requests to the monolith should be made via these functions
// builds off `authDel`, `authGet`, `authPost`, and `authPut`

const monolith = (fn, path, payload, opts = {}) => {
  return fn(
    `${SERVICES.TBT_API_URL}${path}`,
    payload,
    merge(
      {
        // credentials: 'include', // indicates that cookies should be transmitted
      },
      opts,
    ),
  ).then(handle4xx)
}

const _monolithGet = (...args) => monolith(_authGet, ...args) // private function provides an unmolested path

export const monolithDel = (...args) => monolith(authDel, ...args)
export const monolithGet = gettify(_monolithGet) // exported function rejiggers parameters
export const monolithPost = (...args) => monolith(authPost, ...args)
export const monolithPut = (...args) => monolith(authPut, ...args)

// Cookies
// ----------------------------------------------------------------------------

// NOTE: The following function is intended specifically for login and
//  registration requests to the monolith for two reasons: (A) the requests
//  must carry cookies for attribution and (B) the request must NOT carry a JWT
//  because this will trigger a poorly understood code path at
//  `order.can_checkout` in `check_event_order_exist` in `register_request`
//
//  Ideally, the monolith should ignore the JWT on these requests and we can go
//   back to using `monolithPost` instead of `cookiePost`

const cookie = (fn, path, payload, opts = {}) =>
  fn(
    `${SERVICES.TBT_API_URL}${path}`,
    payload,
    merge(
      {
        credentials: 'include', // indicates that cookies should be transmitted
      },
      opts,
    ),
  )

export const cookiePost = (...args) => cookie(post, ...args)
